import React, { useRef, useState } from 'react';
import isNil from 'lodash/isNull';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import ExportToExcelButton from './components/ExportToExcelButton';
import SaveChangesButton from './components/SaveChangesButton';
import DiscardChangesButton from './components/DiscardChangesButton';
import EditButton from './components/EditButton';
import AddButton from './components/AddButton';
import AddMultipleButton from './components/AddMultipleButton';
import RecycleBinToggleButton from './components/RecycleBinToggleButton';
import cn from 'classnames';
import './style.scss';
// cells
import { BooleanCell, PercentCell, DateCell } from '../../Cells';
import { TooltipHeaderCell } from '../../Headers';
import ColumnsToDisplayModal from './components/modals/ColumnsToDisplayModal';
import { FilterButton } from 'shared/buttons';
import { Badge } from '@progress/kendo-react-indicators';
import BaseButton from 'src/shared/components/buttons';
import Modal from 'shared/modals/Modal';
import FilteringPanel from '../../../filter';

const BaseGrid = ({
  children,
  data,
  total,
  sortable,
  sort,
  onSortChange,
  page,
  pageable,
  onPageChange,
  className,
  onRowSelect,
  columns,
  onItemChange,
  onChangesSaved,
  onChangesDiscarded,
  onEdit,
  editable = false,
  editEnabled = false,
  exportEnabled = true,
  addDisabled = false,
  enableColumnsToDisplay,
  columnsToDisplay,
  onAdd,
  handleAdd,
  excelExport,
  isExporting,
  actionComponents,
  recycleBin,
  showRecycleBin,
  hideRecycleBin,
  onAddMultiple,
  pagingButtonCount,
  filteringOptions,
  onFilterClear,
  onFilterApply,
  filterApplied,
  currentFilter,
  name,
  ...props
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [columnsToDisplayOpened, setColumnsToDisplayOpened] = useState(false);

  const _export = useRef(null);
  const _grid = useRef();
  const defaultExcelExport = () => {
    if (_export.current !== null) {
      _export.current.save(data, _grid.current.columns);
    }
  };

  const gridClassName = cn('old-base-grid', {
    [className]: className,
  });

  const onSelectionChange = ({ dataItems, endRowIndex }) => {
    if (onRowSelect) {
      const selectedDataItem = dataItems[endRowIndex];
      onRowSelect(selectedDataItem);
    }
  };

  const handleFilterApplied = ({ filter }) => {
    setShowFilter(false);
    onFilterApply(filter);
  };

  const handleClearFilter = () => {
    onFilterClear();
  };

  return (
    <>
      {!excelExport && <ExcelExport ref={_export} />}
      <Tooltip openDelay={100} position='right' anchorElement='pointer'>
        <Grid
          ref={_grid}
          resizable
          data={data}
          sortable={sortable}
          sort={sort}
          onSortChange={onSortChange}
          skip={page?.skip}
          pageSize={page?.take}
          total={total}
          pageable={isNil(pageable) ? {
            buttonCount: pagingButtonCount ?? 5,
            info: true,
            type: 'numeric',
            pageSizes: [10, 25, 50, 100],
            previousNext: true,
          } : pageable}
          onItemChange={onItemChange}
          onPageChange={onPageChange}
          className={gridClassName}
          selectable={!!onRowSelect}
          onSelectionChange={onSelectionChange}
          {...props}
        >
          {columns &&
            columns.map((c) => {
              const props = { ...c };

              if (c.isPercent) {
                props.cell = ({ dataItem, ...props }) => <PercentCell value={dataItem[c.field]} {...props} />;
              }
              else if (c.isDate) {
                props.cell = ({ dataItem, ...props }) => <DateCell date={dataItem[c.field]} {...props} />;
              }
              else if (c.isBoolean) {
                props.cell = ({ dataItem, field, ...props }) => <BooleanCell value={dataItem[field]} {...props} />;
              }

              if (c.avoidSort) {
                props.headerCell = (props) => <TooltipHeaderCell tooltip='Not sortable' {...props} />
              }
              return !c.hide && <GridColumn key={c.field} {...props} />;
            })}
          {children}
          {(editable || exportEnabled) && (
            <GridToolbar>
              {enableColumnsToDisplay && <BaseButton title='Columns To Display' onClick={() => setColumnsToDisplayOpened(true)} />}
              {handleAdd && !recycleBin && <AddButton onAdd={handleAdd} />}
              {!recycleBin && actionComponents}
              {showRecycleBin && <RecycleBinToggleButton name={name} value={recycleBin} onShow={showRecycleBin} onHide={hideRecycleBin} />}
              {exportEnabled && <ExportToExcelButton onExport={excelExport ?? defaultExcelExport} />}
              {editable && onAdd && !addDisabled && <AddButton onAdd={onAdd} />}
              {editable && onAddMultiple && !addDisabled && <AddMultipleButton onAdd={onAddMultiple} />}
              {editable && onEdit && <EditButton onEdit={onEdit} editEnabled={editEnabled} />}
              {editable && onChangesDiscarded && <DiscardChangesButton onDiscard={onChangesDiscarded} />}
              {editable && onChangesSaved && <SaveChangesButton onSave={onChangesSaved} />}
              {filteringOptions && (
                <>
                  <div>
                    {filterApplied && <span className="k-icon notifications">
                      <Badge shape="dot" themeColor="error" />
                    </span>}
                    <FilterButton onClick={() => setShowFilter(true)} />
                  </div>
                  {filterApplied && <BaseButton title='Clear Filter' onClick={handleClearFilter} />}
                </>
              )}
            </GridToolbar>
          )}
        </Grid>
      </Tooltip>

      <ColumnsToDisplayModal
        isOpen={columnsToDisplayOpened}
        close={() => setColumnsToDisplayOpened(false)}
        columnsToDisplay={columnsToDisplay}
        onColumnDisplayingChanged={props.onColumnDisplayingChanged}
      />

      {filteringOptions && (
        <Modal
          isOpen={showFilter}
          handleClose={() => setShowFilter(false)}
          containerStyle='medium'
          title='Filtering'
        >
          <FilteringPanel
            entity={filteringOptions.entity}
            onApply={handleFilterApplied}
            initialFilter={currentFilter}
          />
        </Modal>
      )}
    </>
  );
};

export default BaseGrid;
